import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, Eye, EyeOff, Mail, Lock, User, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { SiGoogle } from 'react-icons/si';

type Mode = 'login' | 'register';

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { user, signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { toast.error('请填写邮箱和密码'); return; }
    setLoading(true);
    try {
      if (mode === 'login') {
        const { error } = await signInWithEmail(email, password);
        if (error) { toast.error('登录失败：' + (error.message || '账号或密码错误')); }
        else { toast.success('登录成功，欢迎回来！'); navigate('/dashboard'); }
      } else {
        if (!fullName) { toast.error('请填写姓名'); setLoading(false); return; }
        if (password.length < 6) { toast.error('密码至少需要 6 位'); setLoading(false); return; }
        const { error } = await signUpWithEmail(email, password, fullName);
        if (error) { toast.error('注册失败：' + (error.message || '请检查邮箱格式')); }
        else { toast.success('注册成功！请查收验证邮件后登录'); setMode('login'); }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const { error } = await signInWithGoogle();
    if (error) { toast.error('Google 登录失败，请重试'); setGoogleLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Left panel */}
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, oklch(0.18 0.10 240) 0%, oklch(0.30 0.15 240) 60%, oklch(0.38 0.16 240) 100%)' }}>
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        {/* Gold accent */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'oklch(0.72 0.08 45)', filter: 'blur(80px)' }} />

        {/* Logo */}
        <Link to="/" className="relative flex items-center gap-3 group w-fit">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-white" style={{ fontFamily: "'Noto Serif SC', serif" }}>长安大学</div>
            <div className="text-xs text-white/60 tracking-widest">CHANG'AN UNIVERSITY</div>
          </div>
        </Link>

        {/* Center content */}
        <div className="relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}>
            <div className="text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              厚德博学<br />笃行致远
            </div>
            <p className="text-white/70 text-lg leading-relaxed max-w-sm">
              登录长安大学校园门户，获取个性化学术资源、教务管理和校园综合服务。
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-10 grid grid-cols-3 gap-4">
            {[
              { v: '36000+', l: '在校学生' },
              { v: '2800+', l: '专任教师' },
              { v: '80个', l: '本科专业' },
            ].map(s => (
              <div key={s.l} className="bg-white/10 rounded-xl p-4 text-center border border-white/10">
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-mono)' }}>{s.v}</div>
                <div className="text-xs text-white/60 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Bottom */}
        <div className="relative text-xs text-white/40">© 2024 长安大学 版权所有</div>
      </motion.div>

      {/* Right panel - Form */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 bg-background">

        {/* Mobile back */}
        <Link to="/" className="lg:hidden self-start flex items-center gap-2 text-sm text-muted-foreground mb-8 hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" /> 返回主页
        </Link>

        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-bold text-lg" style={{ fontFamily: "'Noto Serif SC', serif" }}>长安大学</div>
              <div className="text-xs text-muted-foreground">CHANG'AN UNIVERSITY</div>
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              {mode === 'login' ? '欢迎回来' : '创建账号'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === 'login' ? '登录您的长安大学校园账号' : '注册长安大学校园门户账号'}
            </p>
          </div>

          {/* Google Login */}
          <Button
            variant="outline"
            className="w-full h-11 flex items-center gap-3 border-border hover:bg-secondary transition-all font-medium mb-6"
            onClick={handleGoogle}
            disabled={googleLoading || loading}>
            {googleLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <SiGoogle className="w-4 h-4 text-[#4285F4]" />
            }
            <span>{googleLoading ? '正在跳转...' : '使用 Google 账号登录'}</span>
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground px-2">或使用邮箱</span>
            <Separator className="flex-1" />
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-lg p-1 mb-6" style={{ background: 'oklch(0.94 0.008 240)' }}>
            {(['login', 'register'] as Mode[]).map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${mode === m ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                {m === 'login' ? '登录' : '注册'}
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <AnimatePresence>
              {mode === 'register' && (
                <motion.div key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <Label htmlFor="name" className="text-sm font-medium mb-1.5 block">姓名</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="name" placeholder="请输入您的姓名" value={fullName} onChange={e => setFullName(e.target.value)}
                      className="pl-9 h-11" disabled={loading} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-1.5 block">邮箱地址</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="请输入邮箱" value={email} onChange={e => setEmail(e.target.value)}
                  className="pl-9 h-11" disabled={loading} autoComplete="email" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-sm font-medium">密码</Label>
                {mode === 'login' && <a href="#" className="text-xs text-primary hover:underline">忘记密码？</a>}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type={showPwd ? 'text' : 'password'} placeholder={mode === 'register' ? '至少 6 位密码' : '请输入密码'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-9 pr-10 h-11" disabled={loading} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full h-11 bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-all"
              disabled={loading || googleLoading}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />处理中...</> : mode === 'login' ? '登录' : '注册账号'}
            </Button>
          </form>

          {/* Switch mode text */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === 'login' ? '还没有账号？' : '已有账号？'}
            <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-primary hover:underline font-medium ml-1">
              {mode === 'login' ? '立即注册' : '返回登录'}
            </button>
          </p>

          <p className="text-center text-xs text-muted-foreground mt-4">
            登录即表示同意长安大学
            <a href="#" className="text-primary hover:underline mx-1">用户协议</a>和
            <a href="#" className="text-primary hover:underline mx-1">隐私政策</a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
